import * as BridgeBase from './bridgebase';
import { EventBatcher, SendResult } from './eventbatcher';
import { jsonStringifyNoThrow } from './util';
const DEFAULT_URL = 'https://ojx6tybe7d.execute-api.eu-central-1.amazonaws.com/production/trackEvents';
export class Destination extends BridgeBase.Destination {
    /**
     * Initialize the destination object
     * @param options
     * @param options.apiKey endpoint API key (used in http header 'x-api-key)
     * @param options.url optional endpoint URL
     * @param options.batching optional event batching configuration
     * @param options.batching.enabled set to false to disable batching (default: true)
     * @param options.batching.flushIntervalMs batch flush interval in milliseconds (default: 3000)
     * @param options.batching.maxBatchSize max number of batched events before forcing a flush (default: 20)
     * @param options.batching.discardThreshold max batch size before events are discarded (default: 200)
     * @param options.batching.flushOnVisibilityChangeHidden control whether events should be flushed on visibilitychange==='hidden' (default: true)
     * @param options.batching.callOnVisibilityChangeHidden optional function to call before events are flushed on visibilitychange==='hidden'
     */
    constructor(options) {
        super(options);
        const { apiKey, url = DEFAULT_URL, batching = {} } = options;
        this.url = url;
        this.apiKey = apiKey;
        const { enabled: batchingEnabled = true } = batching;
        if (batchingEnabled) {
            this.eventBatcher = new EventBatcher(Object.assign(Object.assign({}, batching), { logger: this.logger, sendFunc: (events) => this.sendEvents(events) }));
            this.eventBatcher.start();
        }
        this.logger.debug('=> Softgames.Destination.constructor()');
    }
    /**
     * Send an array of events to our event tracking backend. This will
     * usually be called in the background from the event batcher, or
     * otherwise (if batching is disabled) direct from the logEvent()
     * function.
     *
     * NOTE: this method may never throw!
     *
     * @param batchedPayload an array of batched event objects
     */
    async sendEvents(batchedPayload) {
        if (window.fetch !== undefined) {
            this.logger.debug(`Softgames.Destination.sendEvents(): sending ${batchedPayload.length} events`);
            const body = jsonStringifyNoThrow(batchedPayload);
            if (body === undefined) {
                this.logger.warn('Softgames.Destination.sendEvents(): failed to json-stringify event payload');
                return SendResult.FailedDiscardEvents;
            }
            const options = {
                method: 'PUT',
                headers: {
                    'x-api-key': this.apiKey,
                    'Content-Type': 'application/json',
                },
                body,
            };
            try {
                const response = await fetch(this.url, options);
                this.logger.debug(`Softgames.Destination.sendEvents(): response with status ${response.status}`);
                const json = (await response.json());
                this.logger.debug(json);
                if (response.status !== 200) {
                    return SendResult.SendFailed;
                }
                return SendResult.Success;
            }
            catch (err) {
                this.logger.error('Softgames.Destination.sendEvents(): fetch call failed with ', err);
                return SendResult.SendFailed;
            }
        }
        else {
            this.logger.warn('Softgames.Destination.sendEvents(): fetch API not supported!');
            return SendResult.FailedDiscardEvents;
        }
    }
    /**
     * Triggered during Avo initialization. Here you will usually initialize an analytics SDK
     * with the provided apiKey parameter. You can adjust the initialization based on the env parameter.
     *
     * @remarks if you have already initialized your destination you can leave this callback
     * method empty or leave it out of the interface.
     *
     * @param _env current environment
     * @param _apiKey the API key is always empty for some reason
     */
    make(env, _apiKey) {
        this.logger.debug(`=> Softgames.Destination.make(${env}, [apiKey])`);
    }
    /**
     * This callback is invoked when an Avo function with Log Event action is called. Here you perform the
     * actual event tracking, calling the track/log methods of your analytics destination. Event name
     * and event properties are provided as parameters.
     *
     * @param eventName
     * @param eventProperties
     */
    logEvent(eventName, eventProperties) {
        // wrap into name/payload event as expected by our backend
        const ev = { payload: eventProperties, eventName };
        if (this.eventBatcher !== undefined) {
            this.eventBatcher.addEvent(ev);
        }
        else {
            // NOTE: sendEvents() isn't supposed to throw, but let's still make sure no exceptions can escape
            this.sendEvents([ev]).catch((err) => {
                this.logger.warn('Softgames.Destination.logEvent() failed with: ', err);
            });
        }
        this.logger.debug(`=> Softgames.Destination.logEvent: ${jsonStringifyNoThrow(ev)}`);
    }
    /**
     * If you call an Avo Function for an event with the Identify User action in the Tracking Plan, this
     * callback will be invoked. When calling an Avo Function that includes the Identify User action you'll
     * need to provide a user ID. The main use cases are signup and login. Here you would pass the user ID
     * to the analytics SDK for it to create a new user or attach a session to an existing user.
     *
     * @param userId
     */
    identify(userId) {
        this.logger.debug(`=> Softgames.Destination.identify(${userId})`);
    }
    /**
     * If you call an Avo Function for an event with the Unidentify User action in the Tracking Plan,
     * this callback will be invoked. Here you would call a destination SDK method to detach subsequent
     * actions from the currently identified user.
     */
    unidentify() {
        this.logger.debug('=> Softgames.Destination.unidentify()');
    }
    /**
     * If you call an Avo Function for an event with the Log Revenue action in the Tracking Plan, this
     * callback will be invoked. Here you would log the revenue to your analytics destination, many of
     * them have a special method to log revenue.
     *
     * @remarks if your destination does not support revenue tracking, you can leave this callback empty
     *
     * @param amount
     * @param eventProperties
     */
    revenue(amount, eventProperties) {
        this.logger.debug(`=> Softgames.Destination.revenue(${amount}, ${jsonStringifyNoThrow(eventProperties)})`);
    }
    /**
     * If you call an Avo Function for an event with the Log Page View action in the Tracking Plan, this
     * callback will be invoked. Here you would report navigation to your analytics destination.
     *
     * @remarks: if your destination does not support page/screen tracking, you can leave this callback empty
     *
     * @param pageName
     * @param eventProperties
     */
    logPage(pageName, eventProperties) {
        this.logger.debug(`=> Softgames.Destination.logPage(${pageName}, ${jsonStringifyNoThrow(eventProperties)})`);
    }
    /**
     * If you call an Avo Function for an event with the Update User Properties action in the Tracking Plan,
     * this callback will be invoked. Here you would attach user properties to the currently identified user
     * in your analytics platform.
     *
     * @remarks: if your destination does not support user properties / traits tracking, you can leave this callback empty
     *
     * @param userId
     * @param userProperties
     */
    setUserProperties(userId, userProperties) {
        this.logger.debug(`=> Softgames.Destination.setUserProperties(${userId}, ${jsonStringifyNoThrow(userProperties)})`);
    }
    /**
     * You can update various group properties along with events in the Tracking Plan. If you call an Avo Function
     * for an event with the Update Groups action in the Tracking Plan, this callback is invoked. Here you would
     * update the properties for specified groups.
     *
     * @remarks if your destination does not support groups / accounts property tracking, you can leave this callback empty
     *
     * @param groupType
     * @param groupId
     * @param groupProperties
     */
    setGroupProperties(groupType, groupId, groupProperties) {
        this.logger.debug(`=> Softgames.Destination.setGroupProperties(${groupType}, ${groupId}, ${jsonStringifyNoThrow(groupProperties)})`);
    }
    /**
     * You can add groups and group properties to an event and set the event to associate the currently identified
     * user with the provided groups. Can optionally update group properties along the way.
     *
     * @remarks if your destination does not support groups / accounts property tracking, you can leave this callback empty
     *
     * @param groupType
     * @param groupId
     * @param groupProperties
     */
    addCurrentUserToGroup(groupType, groupId, groupProperties) {
        this.logger.debug(`=> Softgames.Destination.addCurrentUserToGroup(${groupType}, ${groupId}, ${jsonStringifyNoThrow(groupProperties)})`);
    }
    /**
     * This call is same as the logEvent call described above, except it also associates this particular
     * event with a group
     *
     * @remarks if your destination does not support groups / accounts property tracking, you can leave this callback empty
     *
     * @param eventName
     * @param eventProperties
     * @param groupTypesToGroupIds
     */
    logEventWithGroups(eventName, eventProperties, groupTypesToGroupIds) {
        this.logger.debug(`=> Softgames.Destination.addCurrentUserToGroup(${eventName}, ${jsonStringifyNoThrow(eventProperties)}, ${jsonStringifyNoThrow(groupTypesToGroupIds)})`);
    }
}
//# sourceMappingURL=softgames.js.map